// THE CLOSED `kind` VOCABULARY — the discriminator every `task_run_events` row carries.
// Text rather than a pgEnum on purpose: this schema is the single gate, not the DB column.
// Split out of `../task-run-event.ts`, which is now the barrel over this directory.

import { z } from 'zod';
import { RUN_PROVENANCE_EVENT_KIND } from '../run-provenance';

export const TaskRunEventKind = {
  /** The runner invoked a tool (file read/write, search, shell, …). */
  ToolCall: 'tool_call',
  /** An internal reasoning / scratchpad step (CoT trace). */
  Reasoning: 'reasoning',
  /** A file was opened/read. */
  FileRead: 'file_read',
  /** A file was written or created. */
  FileWrite: 'file_write',
  /** A file was deleted. */
  FileDelete: 'file_delete',
  /** A shell command was executed. */
  Command: 'command',
  /** A test suite was executed; payload carries exit code + summary. */
  TestRun: 'test_run',
  /** A git operation (commit, push, pr create, …). */
  GitOp: 'git_op',
  /** A clarifying message the runner surfaced to the human (if escalating). */
  HumanMessage: 'human_message',
  /** The agent's own prose response (e.g. a plan narration). */
  AgentResponse: 'agent_response',
  /** An unhandled error or exception in the runner. */
  Error: 'error',
  /** A lifecycle status ping (e.g. `run_started`, `run_finished`). */
  Status: 'status',
  /** A durable intermediate snapshot the runner can resume from. */
  Checkpoint: 'checkpoint',
  /**
   * A non-PR deliverable (doc 04): the markdown report + artifact references a
   * `report` / `ops_action` task streams into run events INSTEAD of opening a
   * PR. The babysit watch is skipped for these; this event IS the outcome.
   */
  Report: 'report',
  /**
   * A CI jit-exec run's log-archive report (arc 101 slice 03a). UNLIKE every other
   * kind this is TASK-LESS: a CI dispatch has no `tasks` row, so the event carries no
   * `task_id` and is NEVER persisted to `task_run_events`. The event-sink recognises
   * it, records the archived pointer onto the `ci_jobs` row (keyed on `ci_job_id` in
   * the `CiLogReport` payload) + audits it, and skips the run-event chain entirely.
   */
  CiLog: 'ci_log',
  /**
   * Which model/provider/harness the box ACTUALLY drove for one stretch of the run
   * (#2140) — payload {@link RunProvenanceSchema}. A kind of its own rather than a
   * `status` sub-event: it moves no task and marks no stage, and the PR-body read
   * filters this column instead of scanning every `status` payload. A run emits one
   * per provider switch, so the consumers read them in `seq` order, never last-wins.
   */
  RunProvenance: RUN_PROVENANCE_EVENT_KIND,
  /**
   * What one run SPENT — payload {@link RunUsagePayloadSchema} (#2297). A kind of
   * its own for the same two reasons `run_provenance` is one: it moves no task and
   * marks no stage, and the read that totals a PR's spend filters an indexed column
   * instead of scanning `payload ->> 'event'` across the `status` rows that dominate
   * the table. Emitted ONCE per run — the sibling of the provenance event that names
   * WHAT drove the run, where this one names what that cost.
   *
   * Deliberately ABSENT from `prune-run-event-payloads.ts`'s redaction set: a usage
   * payload is counts and a model id, never model-authored text, and pruning it at
   * 72h would silently reset a run's spend to unknown.
   */
  Usage: 'usage',
  /**
   * The box's ATTESTATION that a run's `usage` rows are complete (#3881) — payload
   * {@link RunUsageLedgerSchema}. Emitted ONCE per run, before its terminal event.
   * `passes === usage_rows` (and that many `usage` rows persisted) is the only reading
   * under which a run's usage rows are its whole model spend; no ledger, or unequal
   * counts, means the spend is unknown. A `run_provenance` row proves nothing of the
   * sort: older binaries emit provenance and left passes unmetered, a usage publish can
   * fail, and a pass that counted no step publishes no row.
   *
   * ABSENT from `prune-run-event-payloads.ts`'s redaction set for the reason `usage`
   * is: two counts, and pruning them would reset every run's completeness to unknown.
   */
  UsageLedger: 'usage_ledger',
} as const;

export const TaskRunEventKindSchema = z.enum(TaskRunEventKind);
export type TaskRunEventKind = z.infer<typeof TaskRunEventKindSchema>;
